"use client";

import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { useUser } from "@/shared/contexts/UserContext";

export default function CookiePolicyPage() {
  const { isAuthenticated } = useUser();

  return (
    <div className="min-h-screen bg-cream">
      <header className="flex justify-between items-center p-4">
        <Link
          href="/"
          className="font-patrick text-2xl md:text-3xl text-charcoal font-bold"
        >
          <div
            className="font-winky text-3xl text-charcoal font-black p-0 m-0"
          >
            <img src="/logo.png" alt="Hey Hoku" width={256} height={263} className="h-10 w-auto" />
          </div>
        </Link>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <Button
              asChild
              className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-sm px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
            >
              <Link href="/user/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <>
              <Button
                asChild
                className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-sm px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
              >
                <Link href="/login?action=signup">Sign Up</Link>
              </Button>
              <Button
                asChild
                className="button-3d rounded-lg bg-[#ffd372] text-charcoal hover:bg-yellow hover:text-black delay-200 ease-initial font-winky text-base px-5 py-2 border-1 border-[#4c4c4c] transition-all duration-200 transform hover:scale-105 active:scale-95 active:translate-y-[2px] shadow-[2px_2px_0_#4c4c4c]"
              >
                <Link href="/login">Login</Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-[#ffd372]">
          <h1 className="font-winky text-4xl font-bold text-charcoal mb-8">
            Cookie Policy
          </h1>

          <div className="prose prose-lg max-w-none font-winky text-charcoal space-y-6">
            <p className="text-sm text-gray-600 mb-6">
              Effective Date: August 18, 2025
            </p>

            <p>
              This Cookie Policy explains how Hey Hoku ("we", "us", "our") uses
              cookies and similar tracking technologies when you visit our
              website or use our web or progressive web app.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              1. WHAT ARE COOKIES?
            </h2>
            <p>
              Cookies are small data files placed on your device to facilitate
              navigation, enhance user experience, remember preferences, analyze
              usage patterns, and personalize advertising.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              2. TYPES OF COOKIES WE USE
            </h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                <strong>Session Cookies:</strong> Temporary, deleted when
                browser/app is closed, used to enable basic functions (e.g.,
                login, navigation).
              </li>
              <li>
                <strong>Persistent Cookies:</strong> Remain on device for a
                defined period; store preferences and analytics data.
              </li>
              <li>
                <strong>Own Cookies:</strong> Set and managed by Hey Hoku for
                authentication and preferences.
              </li>
              <li>
                <strong>Third-Party Cookies:</strong> Set by service providers
                (Google Analytics, Meta, Vercel) for analytics and marketing
                purposes.
              </li>
            </ul>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              3. PURPOSES OF COOKIES
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 mt-4">
                <thead>
                  <tr className="bg-[#ffd372]">
                    <th className="border border-gray-300 p-3 text-left">
                      Name
                    </th>
                    <th className="border border-gray-300 p-3 text-left">
                      Expiration
                    </th>
                    <th className="border border-gray-300 p-3 text-left">
                      Purpose
                    </th>
                    <th className="border border-gray-300 p-3 text-left">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3">
                      heyhoku.sessionId
                    </td>
                    <td className="border border-gray-300 p-3">1 Week</td>
                    <td className="border border-gray-300 p-3">
                      Stores user session ID after log-in
                    </td>
                    <td className="border border-gray-300 p-3">Own</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">
                      heyhoku.accepted.cookies
                    </td>
                    <td className="border border-gray-300 p-3">5 Years</td>
                    <td className="border border-gray-300 p-3">
                      Set to 'true' when policy is accepted
                    </td>
                    <td className="border border-gray-300 p-3">Own</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">
                      _ga (Google Analytics)
                    </td>
                    <td className="border border-gray-300 p-3">2 Years</td>
                    <td className="border border-gray-300 p-3">
                      Tracks user behavior and generates analytics
                    </td>
                    <td className="border border-gray-300 p-3">Third Party</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">
                      meta_pixel_id
                    </td>
                    <td className="border border-gray-300 p-3">Session</td>
                    <td className="border border-gray-300 p-3">
                      Marketing, ad efficacy, retargeting
                    </td>
                    <td className="border border-gray-300 p-3">Third Party</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="font-bold text-2xl mt-8 mb-4">
              4. CONTROLLING YOUR COOKIE PREFERENCES
            </h2>
            <p>
              Most browsers allow management of cookies via settings
              (Privacy/Options).
            </p>
            <p>
              You may block, delete, or restrict cookies. Some features of Hey
              Hoku may not function properly if cookies are disabled.
            </p>

            <h2 className="font-bold text-2xl mt-8 mb-4">7. CONTACT</h2>
            <p>
              For questions or concerns about cookie use:
              <br />
              swethaa@heyhoku.ai / lilia@liliafashionhouse.com
            </p>
          </div>

          <nav className="mt-16 border-t border-[#eee] pt-8">
            <p className="font-winky text-[13px] text-[#767676] mb-3">Related policies</p>
            <div className="flex flex-wrap gap-4">
              <Link href="/legal/privacy" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Privacy Policy</Link>
              <Link href="/legal/terms" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Terms of Service</Link>
              <Link href="/support" className="font-winky text-[14px] text-[#2d2d2d] underline decoration-[#ffd372] decoration-2 underline-offset-2 hover:text-[#c0392b] transition-colors">Contact Support</Link>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}
